import { useState } from 'react';

import {
  useFloating,
  useInteractions,
  shift,
  offset,
  flip,
  size,
  useClick,
  useDismiss,
  useRole,
  OffsetOptions,
  autoUpdate,
  Placement,
} from '@floating-ui/react';

interface UsePopoutOptions {
  placement?: Placement;
  offset?: OffsetOptions;
  onClose?: () => void;
  // Floors the floating panel's width at the reference element's width —
  // for a combobox whose trigger is a pill showing the current value
  // (single/multi-select filters), so the dropdown reads as "this pill,
  // opened" rather than an unrelated-width panel floating below it. A
  // floor rather than an exact match: a short pill (e.g. an unselected
  // filter showing just its name, no value yet) would otherwise force its
  // dropdown down to that same narrow width regardless of what the search
  // input or option labels actually need, squeezing/truncating them. Off
  // by default: most consumers (e.g. the "Add ..." combobox variants)
  // trigger off a small icon button and want their own comfortable width
  // regardless of the trigger's size.
  matchReferenceWidth?: boolean;
  // CSS selector for content that should NOT count as "outside" this
  // popout, even though it isn't a DOM descendant of the floating panel.
  // For a floating panel that renders a third-party widget with its own
  // nested popover/dropdown portaled elsewhere in the DOM (e.g. baseui's
  // Calendar, whose month/year <Select> portals its menu to a separate
  // node, not inside our own FloatingPortal) — without this, picking a
  // month or year registers as an outside press and dismisses the whole
  // popout, discarding whatever was in progress inside it. Off by default:
  // most consumers have no such nested widget, and broadening what counts
  // as "inside" is only correct when there's a specific, known element to
  // name.
  ignoreOutsidePressWithin?: string;
  // ARIA role for the reference/floating pair (useRole). Wires up
  // aria-haspopup/aria-expanded on the trigger and the matching role on the
  // panel. Opt-in, not a default: this hook backs Tab-navigable panels
  // (role: 'dialog' -- the header's bell/avatar menus, which have no
  // menuitem children or arrow-key navigation, so 'menu' would overclaim),
  // search/select comboboxes ('listbox'/'menu' depending on the pattern),
  // and third-party widgets (baseui's Calendar, which manages its own ARIA
  // and shouldn't get a role imposed on top of it), so there's no single
  // correct default across consumers.
  role?: 'menu' | 'listbox' | 'dialog' | 'tooltip' | 'alertdialog' | 'grid' | 'tree';
}

export function usePopout(options?: UsePopoutOptions) {
  const [isOpen, setIsOpen] = useState(false);

  function onOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      options?.onClose?.();
    }
  }

  const { refs, x, y, strategy, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement: options?.placement || 'bottom-start',
    middleware: [
      shift(),
      flip(),
      offset(options?.offset || 10),
      // Always on, not just for matchReferenceWidth: flip() picks whichever
      // side (above/below the trigger) has more room, but doesn't stop the
      // panel from being taller than that room actually is — a trigger
      // near the top of a short viewport can have too little space both
      // above and below for the panel's natural height either way. Caps
      // maxHeight at whatever space the chosen placement actually has, and
      // floatingStyles below adds overflowY: auto so content beyond that
      // scrolls inside the panel instead of overflowing the viewport.
      size({
        padding: 8,
        apply({ availableHeight, rects, elements }) {
          elements.floating.style.maxHeight = `${availableHeight}px`;
          if (options?.matchReferenceWidth) {
            elements.floating.style.minWidth = `${rects.reference.width}px`;
          }
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePress: options?.ignoreOutsidePressWithin
      ? (event) =>
          !(event.target as Element | null)?.closest(options.ignoreOutsidePressWithin as string)
      : true,
  });
  // enabled: false rather than omitting the hook entirely when no role is
  // requested -- useRole (like every floating-ui interaction hook) must be
  // called unconditionally on every render; toggling it in and out of the
  // useInteractions array based on a prop would violate the rules of hooks.
  const role = useRole(context, { enabled: options?.role != null, role: options?.role });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click, role]);

  const floatingStyles: React.CSSProperties = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
    overflowY: 'auto',
  };

  return {
    isOpen,
    setIsOpen,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    refs,
    // Lets a consumer wrap its panel in <FloatingFocusManager context={context}>
    // for proper focus trapping/return (Tab lands on the panel's first focusable
    // element on open, focus returns to the trigger on close) -- without it, a
    // FloatingPortal-rendered panel sits at the end of <body>, so Tab from the
    // trigger falls through to whatever comes next in DOM order instead of into
    // the panel.
    context,
  };
}

export { FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
